import React, { useContext, useEffect, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Layout from '@components/Layout';
import { NavContext } from '@components/navigation/Context';
import PageComponent from '@components/page/PageComponent';
import Colors from '@components/shared/Colors';
import StickyBanner from '@components/shared/StickyBanner';
import SimpleProductCarousel from '@components/storefront/SimpleProductCarousel';
import { DEFAULT_LOCALE } from '@constants';
import { PageSlice } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import handlePageRedirect from '@utils/handlePageRedirect';
import { mapPrismicLinkAlias } from '@utils/mapPrismicAliases';
import { productPageTrack } from '@utils/tracking';
import useNavigate from '@utils/useNavigate';

export const pageQuery = graphql`
  query ($language: String!, $uid: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    allPrismicEcommercePage(filter: { uid: { eq: $uid } }) {
      nodes {
        id
        uid
        _previewable
        lang
        data {
          title {
            text
          }
          shareImage {
            url
          }
          description {
            text
          }
          text_color
          show_products_carousel
          banner_title {
            text
          }
          banner_icon {
            alt
            gatsbyImageData
            url
          }
          banner_button_text {
            text
          }
          banner_enabled
          banner_button_link {
            text
          }
          body {
            ... on PrismicEcommercePageDataBodyImageText {
              slice_type
              items {
                button_text {
                  text
                }
                button_link {
                  text
                }
                button_style
              }
              primary {
                text {
                  html
                }
                padding {
                  text
                }
                mobile_padding {
                  text
                }
                mobile_reverse
                layout
                gap {
                  text
                }
                mobile_gap {
                  text
                }
                mobile_text_align
                image_width {
                  text
                }
                mobile_image_width {
                  text
                }
                text_width {
                  text
                }
                breakpoint {
                  text
                }
                background_color
                background_gradient {
                  text
                }
                border {
                  text
                }
                text_align
                text_color
                alignment
                image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommercePageDataBodyButtonRow {
              slice_type
              primary {
                background_color
                color
              }
              items {
                new_window
                button_text {
                  text
                }
                button_link {
                  text
                }
                button_style
              }
            }
            ... on PrismicEcommercePageDataBodySpacer {
              slice_type
              primary {
                height {
                  text
                }
                height_mobile {
                  text
                }
                background_color
                background {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyCertificates {
              slice_type
            }
            ... on PrismicEcommercePageDataBodyTrustpilot {
              slice_type
              primary {
                background_color
                text_color
                type
                tags {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyImageGrid {
              items {
                image {
                  gatsbyImageData
                  alt
                }
              }
              slice_type
            }
            ... on PrismicEcommercePageDataBodyFeaturedProducts {
              slice_type
              primary {
                background_color
                product_ids {
                  text
                }
                title_key
              }
            }
            ... on PrismicEcommercePageDataBodyVitalsCarousel {
              slice_type
              primary {
                article_ids_string
                hide_title
                show_read_more_links
                heading {
                  text
                }
                background {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyScreeningParametersInfoSlider {
              slice_type
              primary {
                type
              }
            }
            ... on PrismicEcommercePageDataBodyProductComparisonTable {
              slice_type
              primary {
                background_color
                explainer_background_color
                table_json {
                  text
                }
                table_header_text {
                  text
                }
                row_title {
                  text
                }
                row_sub_title
                highlighted_discount {
                  text
                }
                highlighted_column_index
                explainer_text {
                  richText
                }
                explainer_image {
                  gatsbyImageData
                  alt
                  dimensions {
                    width
                    height
                  }
                  url
                }
              }
              id
            }
            ... on PrismicEcommercePageDataBodyFeaturedIn {
              slice_type
              primary {
                featured_title {
                  text
                }
                background_color
                text_color
                type
              }
            }
            ... on PrismicEcommercePageDataBodyIconTextGrid {
              slice_type
              primary {
                grid_title {
                  text
                  richText
                }
                is_small_title
                is_small_item_title
                text_align
                gap {
                  text
                }
                gap_mobile {
                  text
                }
                icon_gap {
                  text
                }
                icon_gap_mobile {
                  text
                }
                column_max_width {
                  text
                }
                alignment_desktop
                alignment_mobile
                columns {
                  text
                }
                image_width {
                  text
                }
                image_height {
                  text
                }
                background_color
                background_gradient {
                  text
                }
                text_color
                expandable
                expandable_text {
                  text
                }
                max_visible_items {
                  text
                }
                is_inner_bordered
                is_inner_top_border_rounded
                is_inner_bottom_border_rounded
                inner_width {
                  text
                }
                inner_max_width {
                  text
                }
                inner_max_width_mobile {
                  text
                }
                inner_background {
                  text
                }
                inner_padding {
                  text
                }
                inner_padding_mobile {
                  text
                }
                scroll_on_mobile
              }
              items {
                image {
                  gatsbyImageData
                  alt
                }
                item_title {
                  text
                  richText
                }
                text {
                  html
                }
                link_alias: link {
                  text
                }
                link_text {
                  text
                }
                new_tab
                type_of_link
              }
            }
            ... on PrismicEcommercePageDataBodyNewsletter {
              slice_type
              primary {
                type
                newsletter_button_text: button_text
                consent_text
                newsletter_id
                image {
                  alt
                  url
                  gatsbyImageData
                }
                newsletter_background: background
                headline {
                  richText
                  text
                }
                body_text
              }
            }
            ... on PrismicEcommercePageDataBodyIconDescriptionBoxes {
              id
              items {
                link_text_key
                link {
                  target
                  url
                }
                title_text_key
                main_text_key
                huge_text_key
                background_style
                icon {
                  url
                  gatsbyImageData
                  alt
                  dimensions {
                    width
                    height
                  }
                  url
                }
              }
              slice_type
              primary {
                mobile_icon_max_width
                icon_max_width
                is_mobile_slider
                background_style
                main_text_font_size
                mobile_main_text_font_size
                column_max_width {
                  text
                }
                heading_icon {
                  alt
                  gatsbyImageData
                  dimensions {
                    width
                    height
                  }
                  url
                }
                heading_text_key
                padding_style
              }
            }
            ... on PrismicEcommercePageDataBodyHowItWorksSteps {
              slice_type
              primary {
                heading_text_key
              }
              items {
                main_text_key
                info_text_key
                video_link_text_key
                video_preview_image {
                  url
                  alt
                }
                image {
                  url
                  alt
                }
                video_url {
                  url
                }
              }
            }
            ... on PrismicEcommercePageDataBodyPositivesAndImage {
              slice_type
              primary {
                heading_text_key
                background_style
                mobile_background_style
                button_href
                button_text_key
                image {
                  url
                  gatsbyImageData
                  alt
                }
              }
              items {
                main_text_key
              }
            }
            ... on PrismicEcommercePageDataBodyImageAndInfo {
              slice_type
              primary {
                aux_image {
                  url
                  alt
                }
                top_heading_text_key
                top_heading_badge_text_key
                heading_text_key
                description_text_key
                button_text_key
                background_style
                mobile_background_style
                heading_font_size
                heading_font_size_mobile
                focus_text_key
                focus_text_description_key
                show_link_instead_of_button
                mobile_align_center
                link
                image {
                  url
                  alt
                }
                desktop_padding
                layout
                mobile_layout
              }
            }
            ... on PrismicEcommercePageDataBodyText {
              slice_type
              primary {
                text {
                  html
                  richText
                }
                maxwidth {
                  text
                }
                background_color
                text_color
                text_align
                text_align_mobile
                font_size
                font_size_mobile
              }
            }
            ... on PrismicEcommercePageDataBodyImage {
              slice_type
              primary {
                image {
                  alt
                  gatsbyImageData
                }
                width {
                  text
                }
                height {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyTamponsModalRow {
              slice_type
            }
            ... on PrismicEcommercePageDataBodySubscriptionModule {
              slice_type
              primary {
                image {
                  gatsbyImageData
                  alt
                }
                show_sampler
                title {
                  text
                }
                background_color
                text_color
              }
              items {
                feature {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyPriorityTips {
              slice_type
              primary {
                heading {
                  text
                }
                padding {
                  text
                }
                background {
                  text
                }
                bottom_info {
                  text
                }
                bottom_info_image {
                  gatsbyImageData
                  alt
                }
              }
              items {
                type
                image {
                  gatsbyImageData
                  alt
                }
                text {
                  text
                }
                priority
              }
            }
            ... on PrismicEcommercePageDataBodyOverlappingItems {
              slice_type
              primary {
                heading {
                  text
                }
                padding {
                  text
                }
                background {
                  text
                }
              }
              items {
                type
                icon {
                  gatsbyImageData
                  alt
                }
                image {
                  gatsbyImageData
                  alt
                }
                heading {
                  text
                }
                text {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyScienceModule {
              slice_type
            }
            ... on PrismicEcommercePageDataBodyEndorsementsRow {
              slice_type
              primary {
                background_color
                background_image {
                  url
                }
                title {
                  text
                }
                text_color
              }
              items {
                name {
                  text
                }
                profile_image {
                  gatsbyImageData
                  alt
                }
                quote {
                  text
                }
                title {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyTestimonialsSlider {
              slice_type
              primary {
                background_color
                text_color
              }
              items {
                review_text {
                  text
                }
                review_cite {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyVideo {
              slice_type
              primary {
                cover
                height {
                  text
                }
                mobile_height {
                  text
                }
                video {
                  url
                }
                mobile_video {
                  url
                }
                width {
                  text
                }
                mobile_width {
                  text
                }
                background_color
              }
            }
            ... on PrismicEcommercePageDataBodyFaqs {
              slice_type
              items {
                show_in_countries {
                  text
                }
                answer {
                  richText
                  text
                }
                question {
                  text
                }
              }
              primary {
                title {
                  text
                }
                background_color
                text_color
              }
            }
            ... on PrismicEcommercePageDataBodyTour {
              slice_type
              primary {
                background_color
                text_color
                image {
                  alt
                  gatsbyImageData
                }
              }
              items {
                title {
                  text
                }
                text {
                  html
                }
              }
            }
            ... on PrismicEcommercePageDataBodyTextFeatureRow {
              slice_type
              primary {
                text_color
                text {
                  html
                }
                feature_heading {
                  text
                }
              }
              items {
                text {
                  text
                }
                link_alias: link {
                  text
                }
                image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommercePageDataBodyEndorsementsRow {
              slice_type
              primary {
                background_color
                background_image {
                  url
                }
                title {
                  text
                }
                text_color
              }
              items {
                name {
                  text
                }
                profile_image {
                  gatsbyImageData
                  alt
                }
                quote {
                  text
                }
                title {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyZeroWaste {
              slice_type
              primary {
                title_one {
                  text
                }
                title_two {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyComparisonTable {
              slice_type
            }
            ... on PrismicEcommercePageDataBodyCard {
              slice_type
              primary {
                text {
                  richText
                }
                background_color
                width {
                  text
                }
                padding {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyTwoColumnGrid {
              slice_type
              primary {
                background_color_1
                background_color_2
                border {
                  text
                }
              }
              items {
                left_text {
                  richText
                }
                right_text {
                  richText
                }
                left_icon {
                  gatsbyImageData
                  alt
                }
                right_icon {
                  gatsbyImageData
                  alt
                }
              }
            }
            ... on PrismicEcommercePageDataBodyWaitlistHeader {
              slice_type
              items {
                icon {
                  alt
                  url
                  gatsbyImageData
                }
                text {
                  html
                }
              }
              primary {
                klaviyo_id {
                  text
                }
                text {
                  html
                }
                subtext {
                  html
                }
                padding {
                  text
                }
                mobile_reverse
                layout
                gap {
                  text
                }
                breakpoint {
                  text
                }
                background_color
                text_color
                alignment
                image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommercePageDataBodyContentCarousel {
              slice_type
              primary {
                background_color
                text_color
                title {
                  text
                }
              }
              items {
                background_color
                text_color
                title {
                  text
                }
                text {
                  richText
                }
                image {
                  alt
                  gatsbyImageData
                }
                icon {
                  alt
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommercePageDataBodyTestimonialsModule {
              slice_type
              primary {
                overline
                heading {
                  richText
                  text
                }
                subheading {
                  richText
                  text
                }
                button_text_key
                button_url
                background_color
                item_color
              }
              items {
                image {
                  url
                }
                testimonial {
                  richText
                }
                person_name
              }
            }
            ... on PrismicEcommercePageDataBodyHeroSlider {
              slice_type
              items {
                background {
                  text
                }
                background_image {
                  url
                }
                title {
                  text
                  richText
                }
                title_max_width
                title_mobile_max_width
                subtitle {
                  text
                }
                info {
                  text
                }
                info_max_width
                info_mobile_max_width
                image {
                  alt
                  gatsbyImageData
                  url
                  dimensions {
                    width
                    height
                  }
                }
                image_mobile_max_width
                image_mobile_padding
                mobile_image {
                  alt
                  gatsbyImageData
                  url
                  dimensions {
                    width
                    height
                  }
                }
                icon_3_text {
                  text
                }
                icon_2_text {
                  text
                }
                icon_3 {
                  alt
                  gatsbyImageData
                  url
                }
                icon_2 {
                  alt
                  url
                  gatsbyImageData
                }
                icon_1_text {
                  text
                }
                icon_1 {
                  alt
                  url
                  gatsbyImageData
                }
                icons_background
                hide_button_2
                hide_mobile_image
                product_code {
                  text
                }
                button_1_text {
                  text
                }
                button_1_link {
                  text
                }
                show_in_countries {
                  text
                }
                aux_image_mobile_max_width
                no_aux_image
                coming_soon
                desktop_text_align_left
                aux_image_max_width
                aux_image_mobile
                aux_image {
                  alt
                  url
                  gatsbyImageData
                }
                mobile_reverse
                image_padding
                button_color
                priorityNum: priority
                design_style
                fsa_hsa_eligible
              }
            }
            ... on PrismicEcommercePageDataBodyHeaderImage {
              slice_type
              primary {
                button_text_key
                button_url
                background_color
                product_code
                subheading {
                  richText
                  text
                }
                heading {
                  richText
                  text
                }
                coming_soon
                waitlist_eu
                waitlist_us
                centered_text
                fsa_hsa_eligible
                image {
                  alt
                  url
                  gatsbyImageData
                }
                mobile_image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommercePageDataBodyHeaderWaitlist {
              slice_type
              primary {
                button_text {
                  text
                }
                button_link {
                  text
                }
                background {
                  text
                }
                subtitle {
                  text
                }
                heading {
                  text
                }
                info {
                  text
                }
                waitlist_id
                coming_soon
                icon_3_text {
                  text
                }
                icon_2_text {
                  text
                }
                icon_3 {
                  alt
                  gatsbyImageData
                  url
                }
                icon_2 {
                  alt
                  url
                  gatsbyImageData
                }
                icon_1_text {
                  text
                }
                icon_1 {
                  alt
                  url
                  gatsbyImageData
                }
                image {
                  alt
                  url
                  gatsbyImageData
                }
                mobile_image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommercePageDataBodyVmsSpecialistCarePrices {
              slice_type
            }
            ... on PrismicEcommercePageDataBodyPpcPackages {
              slice_type
            }
            ... on PrismicEcommercePageDataBodyPpcDidYouKnow {
              slice_type
            }
            ... on PrismicEcommercePageDataBodyVideoHalfRow {
              primary {
                button_text {
                  text
                }
                background_css {
                  text
                }
                button_link {
                  text
                }
                desktop_placeholder_image {
                  gatsbyImageData
                  alt
                  url
                }
                text_color
                subtitle {
                  text
                }
                mobile_video {
                  url
                }
                heading {
                  text
                }
                desktop_video {
                  url
                }
                mobile_text_color
                mobile_placeholder_image {
                  gatsbyImageData
                  alt
                  url
                }
                text_info_type
                mobile_info_covers_video
                mobile_background_css {
                  text
                }
              }
              slice_type
            }
            ... on PrismicEcommercePageDataBodyStiFoundersResults {
              id
              slice_type
            }
            ... on PrismicEcommercePageDataBodyConsultationsExplained {
              id
              slice_type
            }
            ... on PrismicEcommercePageDataBodyConsultationsMeetNurses {
              id
              slice_type
            }
            ... on PrismicEcommercePageDataBodyConsultationsUpsell {
              id
              slice_type
            }
            ... on PrismicEcommercePageDataBodyProductUpsellRow {
              id
              slice_type
              primary {
                product
              }
            }
            ... on PrismicEcommercePageDataBodyCustomSlice {
              id
              slice_type
              primary {
                type
              }
            }
            ... on PrismicEcommercePageDataBodyThreeStatistics {
              slice_type
              primary {
                heading {
                  text
                }
                background_color
                big_number_color
                button_text_key
                button_url
              }
              items {
                image {
                  url
                  alt
                }
                big_number
                stat_description {
                  text
                }
                source_url
              }
            }
            ... on PrismicEcommercePageDataBodySlideAnimation {
              id
              slice_type
              primary {
                intro_heading {
                  text
                }
                intro_subheading
                intro_image {
                  url
                  alt
                }
                anim_image_one {
                  url
                  alt
                }
                anim_image_two {
                  url
                  alt
                }
              }
              items {
                textbox_title {
                  text
                }
                big_number
                textbox_description {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyHeadingMessage {
              slice_type
              primary {
                heading_text {
                  richText
                  text
                }
                background_color
                text_color1
              }
            }
            ... on PrismicEcommercePageDataBodyTextImageCarouselAnimated {
              id
              slice_type
              primary {
                overline
                heading {
                  text
                }
                intro_description {
                  html
                }
                did_you_know_text
                intro_image {
                  url
                  alt
                }
              }
              items {
                overline
                heading {
                  text
                }
                slide_description {
                  html
                }
                image {
                  url
                  alt
                }
              }
            }
            ... on PrismicEcommercePageDataBodyComparisonTable1 {
              id
              slice_type
              primary {
                heading {
                  text
                }
              }
              items {
                descriptions
                image {
                  url
                  alt
                }
              }
            }
            ... on PrismicEcommercePageDataBodyTwoColProducts {
              id
              slice_type
              primary {
                heading {
                  text
                }
                feature_icons {
                  url
                  alt
                }
              }
              items {
                aux_image {
                  url
                  alt
                }
                overline
                heading {
                  text
                }
                image {
                  url
                  alt
                }
                features {
                  html
                }
              }
            }
            ... on PrismicEcommercePageDataBodyVmsMicrobiomeVideo {
              slice_type
              primary {
                microbiome_image {
                  url
                }
                microbiome_title {
                  text
                }
                microbiome_text {
                  text
                }
                microbiome_more {
                  text
                }
                microbiome_video {
                  url
                }
                microbiome_video_width
                microbiome_video_height
                youtube_video_id {
                  text
                }
              }
            }
            ... on PrismicEcommercePageDataBodyVideoGrid {
              slice_type
              primary {
                grid_title {
                  text
                }
                trustpilot
                title_text_color
                frame
                scroll_elements_desktop
                gap {
                  text
                }
                gap_mobile {
                  text
                }
                rounded_corners
                text_align_all
              }
              items {
                thumbnail {
                  url
                  gatsbyImageData
                  alt
                }
                video {
                  url
                }
                text {
                  text
                }
                video_width {
                  text
                }
                video_height {
                  text
                }
                bottom_text
              }
            }
            ... on PrismicEcommercePageDataBodyTimeline {
              slice_type
              primary {
                overline
                heading {
                  text
                  richText
                }
                subheading {
                  text
                }
                button_url
                button_text_key
              }
              items {
                overline
                heading {
                  text
                  richText
                }
                bodycopy {
                  text
                }
                video_preview_image {
                  url
                  alt
                  gatsbyImageData
                }
                image {
                  url
                  alt
                  gatsbyImageData
                }
                video_url {
                  url
                }
                video_link_text_key
                link_icon
              }
            }
            ... on PrismicEcommercePageDataBodyFirework {
              primary {
                type
                max_width {
                  text
                }
                playlist_id
                background_color
                show_in_countries {
                  text
                }
              }
              slice_type
            }
            ... on PrismicEcommercePageDataBodyHeroFullWidthImage {
              slice_type
              primary {
                heading {
                  richText
                  text
                  html
                }
                subheading {
                  richText
                  text
                  html
                }
                show_starting_at
                fsa_hsa_eligible
                button_url
                button_text_key
                dark_text
                dark_button
                align_top_mobile
                background_image {
                  url
                }
                background_image_mobile {
                  url
                }
                product_code
              }
            }
            ... on PrismicEcommercePageDataBodyImageAndContent {
              id
              slice_type
              primary {
                background_color
                overline
                heading {
                  richText
                }
                subheading {
                  richText
                }
                centered_text
                image_full_background
                product_code1
                price_show_in_countries
                fsa_hsa_eligible
                button_url
                button_text_key
                aux_image {
                  url
                }
                image {
                  url
                }
                image_mobile {
                  url
                }
                notice
                reverse_order
                reverse_order_mobile
                original_image_aspect_ratio
                image_max_width_tablet
              }
              items {
                icon {
                  url
                  alt
                }
                icon_text
              }
            }
            ... on PrismicEcommercePageDataBodyTabsFeature {
              id
              slice_type
              primary {
                overline
                heading {
                  richText
                }
                background_color
                centered_text
              }
              items {
                icon {
                  url
                  alt
                }
                icon_text
                image {
                  url
                }
                image_mobile {
                  url
                }
                item_heading {
                  richText
                }
                item_subheading {
                  richText
                }
                button_url
                button_text_key
                item_centered_text
                item_reverse_order
                item_reverse_order_mobile
                background_full_width
                background_color
                background_image {
                  url
                }
              }
            }
            ... on PrismicEcommercePageDataBodyFlexibleGrid {
              slice_type
              items {
                bodycopy {
                  text
                }
                button_url
                buttontext
                heading {
                  richText
                }
                image {
                  gatsbyImageData
                  alt
                  url
                }
                textcolor
                width
              }
              primary {
                overline
                include_overlay
                heading {
                  richText
                }
                background_text
              }
            }
            ... on PrismicEcommercePageDataBodyBannerGrid {
              slice_type
              items {
                textcolor
                overline
                image {
                  alt
                  url
                  gatsbyImageData
                }
                heading {
                  richText
                }
                buttontext
                button_url
                background_text
                background_image {
                  alt
                  url
                  gatsbyImageData
                }
              }
              primary {
                background_text
              }
            }
            ... on PrismicEcommercePageDataBodyLinkTabs {
              slice_type
              primary {
                overline
                heading {
                  richText
                }
                background_text
              }
              items {
                title {
                  richText
                }
                button_url
                image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommercePageDataBodyCarouselModule {
              slice_type
              primary {
                background_color
              }
              items {
                overline
                heading {
                  richText
                }
                subheading {
                  richText
                }
                button_url
                button_text_key
                image {
                  url
                  alt
                }
              }
            }
            ... on PrismicEcommercePageDataBodyDonationHero {
              slice_type
              primary {
                align_top_mobile
                background_image_mobile {
                  alt
                  url
                  gatsbyImageData
                }
                background_image {
                  alt
                  gatsbyImageData
                  url
                }
                button_text_key
                total_amount
                target_amount
                overline
                dark_text
                dark_button
                button_url
                bodytext {
                  richText
                }
              }
            }
          }
        }
      }
    }
  }
`;

const Page = ({ data, pageContext }) => {
  const { allPrismicEcommercePage } = data;
  const { language } = pageContext;

  const getPageData = () => {
    const pages = allPrismicEcommercePage.nodes;
    const currentLocale = pages.find((page) => page.lang === language);
    const fallbackLocale = pages.find((page) => page.lang === DEFAULT_LOCALE);

    return currentLocale || fallbackLocale;
  };

  const [pageObject, setPageObject] = useState(getPageData());

  // Update pageObject when data changes (for previews)
  useEffect(() => {
    setPageObject(getPageData());
  }, [allPrismicEcommercePage]);

  const { country } = useCountry();
  const { navigateWrapper } = useNavigate();
  const { navDispatch } = useContext(NavContext);

  const {
    uid,
    data: {
      body,
      title,
      description,
      shareImage,
      text_color,
      show_products_carousel,
      banner_enabled,
      banner_icon,
      banner_title,
      banner_button_text,
      banner_button_link
    }
  } = pageObject;

  pageObject.data.body = mapPrismicLinkAlias(pageObject.data.body);

  useEffect(() => {
    if (uid === 'period-pain-clinic') {
      productPageTrack('ppc_standard');
    }
    handlePageRedirect(uid, country, navigateWrapper);
  }, [country]);

  const ref = useRef(null);
  const showProductsCarousel = show_products_carousel;
  let stickyBannerData;
  if (banner_enabled) {
    stickyBannerData = {
      title: banner_title.text,
      buttonText: banner_button_text?.text,
      buttonLink: banner_button_link?.text,
      icon: banner_icon,
      textColor: text_color
    };
  }

  const SEO = {
    href: uid === 'index' ? '/' : `/${uid}/`,
    title: title?.text ?? '',
    description: description?.text ?? '',
    image: shareImage?.url ?? ''
  };

  useEffect(() => {
    if (text_color) navDispatch({ type: 'setColor', data: text_color });
    if (uid === 'index') adjustNavigationColor();
  }, []);

  function adjustNavigationColor() {
    navDispatch({ type: 'setColor', data: Colors.navyBlue });
  }

  return (
    <Layout SEO={SEO}>
      <InnerWrapper ref={ref}>
        {stickyBannerData && <StickyBanner data={stickyBannerData} scrollToRef={ref} hideIconMobile={true} />}
        {body.map((slice: PageSlice, index: number) => {
          // Generate a stable unique key using slice properties
          const sliceKey = slice.id || `${slice.slice_type}-${index}`;

          return index === 0 ? (
            <PageComponent key={sliceKey} slice={slice} country={country} />
          ) : (
            <div key={sliceKey} id={`section-${index}`}>
              <PageComponent slice={slice} country={country} />
            </div>
          );
        })}
        {showProductsCarousel && <SimpleProductCarousel country={country} />}
      </InnerWrapper>
    </Layout>
  );
};

export default withPrismicPreview(Page);

Page.propTypes = {
  data: PropTypes.shape({
    allPrismicEcommercePage: PropTypes.object.isRequired
  }).isRequired,
  pageContext: PropTypes.shape({
    language: PropTypes.string.isRequired
  }).isRequired
};

const InnerWrapper = styled.div``;
